/**
 * 汎用フォーマットユーティリティ
 *
 * バイトサイズ、日付表示などの共通フォーマット関数
 */

import { format } from 'date-fns'
import { ja } from 'date-fns/locale'

// ============================================================================
// バイトサイズ
// ============================================================================

/**
 * バイト数を読みやすい形式にフォーマット（B / KB / MB / GB）
 * すべての単位で小数点第一位まで表示する（例: 1.0 MB / 512.5 KB / 0.0 B）
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0.0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

// ============================================================================
// 相対日付
// ============================================================================

/**
 * 日付を「M月d日(E) 今日」「M月d日(E) 昨日」「M月d日(E)」形式でフォーマット
 *
 * 相対表記だけだと何日か分からないので、日付は必ず入れる
 */
export const formatRelativeDate = (date: Date | string): string => {
  const target = typeof date === 'string' ? new Date(date) : date
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const dateLabel = format(target, 'M月d日(E)', { locale: ja })
  if (format(target, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) return `${dateLabel} 今日`
  if (format(target, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) return `${dateLabel} 昨日`
  return dateLabel
}

// ============================================================================
// 相対時間
// ============================================================================

/**
 * タイムスタンプを「たった今」「○分前」「○時間前」「○日前」形式でフォーマット
 */
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'たった今'
  if (diffMins < 60) return `${diffMins}分前`
  if (diffHours < 24) return `${diffHours}時間前`
  if (diffDays < 7) return `${diffDays}日前`
  return new Date(timestamp).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })
}

// ============================================================================
// ロケール日付
// ============================================================================

/**
 * 日付文字列を短い日本語表示にフォーマット（例: 2月11日(火)）
 */
export const formatDateShort = (dateStr: string): string => {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('ja-JP', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    })
  } catch {
    return dateStr
  }
}

/**
 * YYYY-MM-DD を最小表記にフォーマット（例: 2/11、別の年なら 2025/12/31）
 */
export const formatDateCompact = (dateStr: string): string => {
  const date = new Date(`${dateStr}T00:00:00`)
  return format(date, date.getFullYear() === new Date().getFullYear() ? 'M/d' : 'yyyy/M/d')
}

/**
 * タイムスタンプを日時表示にフォーマット（例: 2026/02/11 14:30）
 */
export const formatDateTimeNumeric = (timestamp: number): string => {
  const date = new Date(timestamp)
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
